/**
 * `/<tenantSlug>/admin/issues` — Admin Lab: Issues.
 *
 * The review surface for in-app error collection (ADR-0121). Split-pane in
 * the execution-log house style: left = a filterable list of the tenant's
 * issues; right = the selected issue's detail — stack + console tail behind
 * disclosures, the pin data for manual reports, a status lifecycle control,
 * an events timeline, and a comment box.
 *
 * Backed by the frozen shell API (issuesApi.ts):
 *   GET  /api/shell/issues?status=      (admin.issues.view)
 *   GET  /api/shell/issues/{id}         (admin.issues.view)
 *   POST /api/shell/issues/{id}/events  (admin.issues.manage)
 *
 * A11y (feature-plan a11y contract): the detail is a `role="complementary"`
 * pane; the status filter is a `role="radiogroup"`; there is exactly ONE
 * page-level `sr-only` polite live region for confirmations; failures near a
 * control use `role="alert"`; stack / console tail sit behind
 * `aria-expanded` disclosure buttons over `<pre>`; every control has a real
 * label (not a placeholder).
 */

import { RequirePermission, useAuth } from "@ken/auth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmpty,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  StatusBadge,
} from "@ken/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";

import {
  addIssueEvent,
  getIssue,
  listIssues,
  type Issue,
  type IssueDetail,
  type IssueStatus,
} from "../lib/issuesApi";

export const Route = createFileRoute("/$tenantSlug/admin/issues")({
  component: AdminIssuesRoute,
});

const STATUS_FILTERS = ["all", "open", "confirmed", "fixed", "closed"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const LIFECYCLE: IssueStatus[] = ["open", "confirmed", "fixed", "closed"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function severityTone(severity: string): "default" | "trial" | "overdue" {
  if (severity === "high") return "overdue";
  if (severity === "med") return "trial";
  return "default";
}

// ---------------------------------------------------------------------------
// Page wrapper — permission-gated (admin.issues.view).
// ---------------------------------------------------------------------------

function AdminIssuesRoute(): ReactElement {
  return (
    <RequirePermission slug="admin.issues.view">
      <IssuesWorkbench />
    </RequirePermission>
  );
}

// ---------------------------------------------------------------------------
// Workbench — exported for the render test.
// ---------------------------------------------------------------------------

export function IssuesWorkbench(): ReactElement {
  const { session } = useAuth();
  const canManage = session?.permissions.has("admin.issues.manage") === true;

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [announce, setAnnounce] = useState("");

  const listQuery = useQuery({
    queryKey: ["admin", "issues", statusFilter],
    queryFn: () =>
      listIssues(statusFilter === "all" ? undefined : statusFilter),
  });

  const detailQuery = useQuery({
    queryKey: ["admin", "issue", selectedId],
    queryFn: () => getIssue(selectedId!),
    enabled: selectedId !== null,
  });

  const issues = listQuery.data?.issues ?? [];

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="admin-issues-page">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
          Admin Lab
        </div>
        <h1 className="text-2xl font-semibold mt-1">Issues</h1>
        <p className="text-sm text-[var(--color-text-soft)] mt-1">
          Errors the cockpit filed automatically, plus issues people reported
          by hand. Pick a row to see the stack, console tail, and the exact
          spot that was flagged.
        </p>
      </header>

      {/* One page-level polite live region for confirmations. */}
      <div aria-live="polite" className="sr-only" data-testid="admin-issues-live">
        {announce}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[3fr_4fr] gap-6">
        {/* ---- List pane -------------------------------------------- */}
        <Card data-testid="admin-issues-list">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Reported issues</CardTitle>
            <div
              role="radiogroup"
              aria-label="Filter by status"
              data-testid="admin-issues-filter-status"
              className="flex flex-wrap items-center gap-1.5 mt-3"
            >
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="radio"
                  aria-checked={statusFilter === f}
                  data-testid={`admin-issues-filter-status-${f}`}
                  onClick={() => setStatusFilter(f)}
                  className={
                    "px-3 py-1 text-[12px] font-medium rounded-[var(--radius-full)] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] " +
                    (statusFilter === f
                      ? "bg-[var(--color-brand-soft)] border-transparent text-[var(--color-brand-strong)]"
                      : "bg-[var(--color-surface)] border-[var(--color-border-hairline)] text-[var(--color-text-soft)] hover:text-[var(--color-text-strong)]")
                  }
                >
                  {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable>
              <DataTableHead>
                <DataTableRow>
                  <DataTableHeader>Title</DataTableHeader>
                  <DataTableHeader>Kind</DataTableHeader>
                  <DataTableHeader>Status</DataTableHeader>
                  <DataTableHeader className="text-right">Seen</DataTableHeader>
                  <DataTableHeader>Updated</DataTableHeader>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {listQuery.isLoading ? (
                  <DataTableEmpty>Loading…</DataTableEmpty>
                ) : listQuery.isError ? (
                  <DataTableEmpty>
                    <span role="alert" className="text-[var(--color-accent-red)]">
                      Couldn&apos;t load issues.
                    </span>
                  </DataTableEmpty>
                ) : issues.length === 0 ? (
                  <DataTableEmpty>No issues match this filter.</DataTableEmpty>
                ) : (
                  issues.map((issue) => (
                    <IssueRow
                      key={issue.id}
                      issue={issue}
                      selected={selectedId === issue.id}
                      onSelect={() => setSelectedId(issue.id)}
                    />
                  ))
                )}
              </DataTableBody>
            </DataTable>
          </CardContent>
        </Card>

        {/* ---- Detail pane ------------------------------------------ */}
        <Card
          role="complementary"
          aria-label="Issue detail"
          data-testid="admin-issues-detail"
        >
          <CardHeader>
            <CardTitle className="text-base">Detail</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedId === null ? (
              <div className="text-sm text-[var(--color-text-faint)]">
                Select an issue to see its detail.
              </div>
            ) : detailQuery.isLoading ? (
              <div className="text-sm">Loading…</div>
            ) : detailQuery.isError ? (
              <div className="text-sm text-[var(--color-accent-red)]" role="alert">
                Failed to load this issue.
              </div>
            ) : detailQuery.data ? (
              <IssueDetailView
                issue={detailQuery.data}
                canManage={canManage}
                onAnnounce={setAnnounce}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// List row
// ---------------------------------------------------------------------------

function IssueRow({
  issue,
  selected,
  onSelect,
}: {
  issue: Issue;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    <DataTableRow
      data-testid={`admin-issues-row-${issue.id}`}
      onClick={onSelect}
      aria-selected={selected}
      className={[
        "cursor-pointer",
        selected ? "bg-[oklch(95%_0.02_240_/_0.6)]" : "",
      ].join(" ")}
    >
      <DataTableCell className="text-sm max-w-[280px] truncate">
        {issue.title}
      </DataTableCell>
      <DataTableCell className="whitespace-nowrap">
        <span className="flex items-center gap-1">
          <Chip tone="default">{issue.kind}</Chip>
          <Chip tone={severityTone(issue.severity)}>{issue.severity}</Chip>
        </span>
      </DataTableCell>
      <DataTableCell>
        <StatusBadge status={issue.status} label={issue.status} />
      </DataTableCell>
      <DataTableCell className="text-right tabular-nums text-xs">
        {issue.occurrences}
      </DataTableCell>
      <DataTableCell className="text-xs whitespace-nowrap">
        {formatTimestamp(issue.updated_at)}
      </DataTableCell>
    </DataTableRow>
  );
}

// ---------------------------------------------------------------------------
// Detail view
// ---------------------------------------------------------------------------

function IssueDetailView({
  issue,
  canManage,
  onAnnounce,
}: {
  issue: IssueDetail;
  canManage: boolean;
  onAnnounce: (msg: string) => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const [actionError, setActionError] = useState("");

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ["admin", "issue", issue.id] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "issues"] });
  }

  const statusMutation = useMutation({
    mutationFn: (next: IssueStatus) => addIssueEvent(issue.id, "status", next),
    onSuccess: (_data, next) => {
      setActionError("");
      onAnnounce(`Status set to ${next}.`);
      invalidate();
    },
    onError: () => setActionError("Couldn't update the status."),
  });

  const commentMutation = useMutation({
    mutationFn: (value: string) => addIssueEvent(issue.id, "comment", value),
    onSuccess: () => {
      setActionError("");
      setComment("");
      onAnnounce("Comment posted.");
      invalidate();
    },
    onError: () => setActionError("Couldn't post the comment."),
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <section className="flex items-start justify-between gap-3">
        <h2
          data-testid="admin-issues-detail-title"
          className="text-base font-semibold text-[var(--color-text-strong)] min-w-0"
        >
          {issue.title}
        </h2>
        <span data-testid="admin-issues-detail-status-badge" className="shrink-0">
          <StatusBadge status={issue.status} label={issue.status} />
        </span>
      </section>

      {/* Meta grid */}
      <section className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
        <div>
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Occurrences
          </div>
          <div
            data-testid="admin-issues-detail-occurrences"
            className="tabular-nums"
          >
            {issue.occurrences} · last seen {formatTimestamp(issue.last_seen_at)}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Kind / severity
          </div>
          <div className="flex items-center gap-1">
            <Chip tone="default">{issue.kind}</Chip>
            <Chip tone={severityTone(issue.severity)}>{issue.severity}</Chip>
            <Chip tone="default">{issue.issue_type}</Chip>
          </div>
        </div>
        <div className="col-span-2">
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Route
          </div>
          <div data-testid="admin-issues-detail-route" className="font-mono break-all">
            {issue.route || "—"}
            {issue.url_params ? `?${issue.url_params}` : ""}
          </div>
        </div>
      </section>

      {/* Manual-report narrative */}
      {(issue.did || issue.expected || issue.saw) && (
        <section className="grid grid-cols-1 gap-2 text-sm">
          {issue.did ? (
            <Narrative label="What they did" value={issue.did} />
          ) : null}
          {issue.expected ? (
            <Narrative label="What they expected" value={issue.expected} />
          ) : null}
          {issue.saw ? (
            <Narrative label="What they saw" value={issue.saw} />
          ) : null}
        </section>
      )}

      {/* Pins (manual reports) */}
      {issue.pins && issue.pins.length > 0 && (
        <section data-testid="admin-issues-detail-pins">
          <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)] mb-1">
            Flagged spot{issue.pins.length > 1 ? "s" : ""}
          </div>
          <ul className="flex flex-col gap-1 text-xs">
            {issue.pins.map((pin, i) => (
              <li
                key={i}
                data-testid={`admin-issues-detail-pin-${i}`}
                className="rounded-md border border-[var(--color-border-hairline)] p-2"
              >
                <div>
                  position {Math.round(pin.x * 100)}% ×{" "}
                  {Math.round(pin.y * 100)}%
                </div>
                <div className="font-mono break-all text-[var(--color-text-soft)]">
                  {pin.selector || "—"}
                </div>
                {pin.label ? <div>“{pin.label}”</div> : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Stack + console tail disclosures */}
      <Disclosure
        label="Stack trace"
        testid="admin-issues-detail-stack"
        content={issue.stack}
      />
      <Disclosure
        label="Console tail"
        testid="admin-issues-detail-console-tail"
        content={issue.console_tail}
      />

      {/* Status control */}
      {canManage ? (
        <section>
          <label
            htmlFor="admin-issues-status-select"
            className="block text-xs uppercase tracking-wide text-[var(--color-text-faint)] mb-1"
          >
            Status
          </label>
          <select
            id="admin-issues-status-select"
            data-testid="admin-issues-detail-status-select"
            value={issue.status}
            disabled={statusMutation.isPending}
            onChange={(e) => statusMutation.mutate(e.target.value as IssueStatus)}
            className="h-9 px-2 rounded-md border border-[var(--color-border-hairline)] text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
          >
            {LIFECYCLE.map((s) => (
              <option
                key={s}
                value={s}
                data-testid={`admin-issues-detail-status-option-${s}`}
              >
                {s}
              </option>
            ))}
          </select>
        </section>
      ) : null}

      {/* Events timeline */}
      <section>
        <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)] mb-1">
          Activity
        </div>
        <ul
          data-testid="admin-issues-detail-events-list"
          className="flex flex-col gap-1.5 text-xs"
        >
          {issue.events.length === 0 ? (
            <li className="text-[var(--color-text-faint)]">No activity yet.</li>
          ) : (
            issue.events.map((ev, i) => (
              <li
                key={i}
                className="flex flex-col rounded-md border border-[var(--color-border-hairline)] p-2"
              >
                <span className="text-[var(--color-text-faint)]">
                  {formatTimestamp(ev.ts)} ·{" "}
                  {ev.actor_email ?? ev.actor_user_id ?? "system"} · {ev.action}
                </span>
                <span className="text-[var(--color-text-strong)]">{ev.value}</span>
              </li>
            ))
          )}
        </ul>
      </section>

      {/* Comment box */}
      {canManage ? (
        <section className="flex flex-col gap-2">
          <label
            htmlFor="admin-issues-comment"
            className="block text-xs uppercase tracking-wide text-[var(--color-text-faint)]"
          >
            Add a comment
          </label>
          <textarea
            id="admin-issues-comment"
            data-testid="admin-issues-detail-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-hairline)] p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
          />
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="admin-issues-detail-comment-submit"
              disabled={comment.trim() === "" || commentMutation.isPending}
              onClick={() => commentMutation.mutate(comment.trim())}
              className="h-9 px-4 rounded-[var(--radius-md)] bg-[var(--color-brand)] text-[var(--color-text-on-dark)] text-sm font-medium disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
            >
              {commentMutation.isPending ? "Posting…" : "Post comment"}
            </button>
          </div>
        </section>
      ) : null}

      {actionError ? (
        <p role="alert" className="text-[12px] text-[var(--color-status-overdue-text)]">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}

function Narrative({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
        {label}
      </div>
      <div className="whitespace-pre-wrap text-[var(--color-text-strong)]">
        {value}
      </div>
    </div>
  );
}

function Disclosure({
  label,
  testid,
  content,
}: {
  label: string;
  testid: string;
  content: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <section>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-medium text-[var(--color-brand-strong)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] rounded-[var(--radius-sm)]"
      >
        {open ? "Hide" : "Show"} {label.toLowerCase()}
      </button>
      {open ? (
        <pre
          data-testid={testid}
          className="mt-1 p-2 rounded bg-[oklch(98%_0.003_230_/_0.7)] overflow-x-auto text-[11px] whitespace-pre-wrap break-all max-h-64"
        >
          {content || "—"}
        </pre>
      ) : null}
    </section>
  );
}
